import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { IssueStatus, LargeImageResultState } from "@/types/technicalAudit";

export type IssueUiStatus =
	| IssueStatus
	| "ready"
	| "needs_attention"
	// Display-only: fix_status is 'manual' but the ticket's started_at is set —
	// an expert is actively working the issue right now.
	| "manual_in_progress";

interface StatusBadgeProps {
	status: IssueUiStatus;
	size?: "sm" | "md";
	resultState?: LargeImageResultState | null;
	// Review-only findings have no automatic fix. Relabel so they do not read as
	// queued work ("Pending") or a negative outcome ("Skipped"/"Rejected").
	reviewOnly?: boolean;
}

const statusConfig: Record<IssueUiStatus, { label: string; className: string }> =
	{
		pending: {
			label: "Pending",
			className: "bg-gray-500 text-white hover:bg-gray-600",
		},
	ready: {
		label: "Suggested",
		className: "bg-teal text-white hover:bg-teal-hover",
	},
	approved: {
		label: "Suggested",
		className: "bg-teal text-white hover:bg-teal-hover",
	},
	rejected: {
		label: "Skipped",
		className: "bg-destructive text-white hover:bg-destructive/90",
	},
		applied: {
			label: "Applied",
			className: "bg-coral text-white hover:bg-coral-hover",
		},
		// Recommendation-only: guidance was saved but NOTHING was written to
		// WordPress. A calm, non-solid tone (NOT the solid coral "Applied") so it
		// never reads as "your live site changed."
		recommended: {
			label: "Recommendation saved",
			className:
				"border border-coral/40 bg-coral-light text-coral-dark hover:bg-coral-light",
		},
		verified: {
			label: "Verified",
			className: "bg-teal-dark text-white hover:bg-teal",
		},
		failed: {
			label: "Failed",
			className: "bg-destructive text-white hover:bg-destructive/90",
		},
		skipped: {
			label: "Skipped",
			className: "bg-muted text-foreground hover:bg-muted",
		},
		protected: {
			label: "Protected",
			className: "bg-coral-light text-coral-dark hover:bg-coral-light",
		},
		// Terminal resolution: the issue's source page was deleted (404) after the
		// crawl, so there is nothing left to fix. Calm/neutral tone — NOT the red
		// destructive "Failed" styling, since this is a resolution, not a failure.
		obsolete: {
			label: "Obsolete",
			className: "bg-muted text-muted-foreground hover:bg-muted",
		},
		// Handed off for a human fix — a ticket was filed to the AgileCo team.
		// Neutral grey (theme-stable `muted` tokens — never brand-repaintable
		// ones): the request is queued, nothing has happened yet. Bordered soft
		// chip so it reads distinct from the solid-grey "Pending". Green is
		// reserved for the completed states (manual_applied / verified).
		manual: {
			label: "Expert fix requested",
			className:
				"border border-border bg-muted text-muted-foreground hover:bg-muted",
		},
		// An expert picked the ticket up and is actively working it (display-only
		// derivation from the ticket's started_at; the stored status stays
		// 'manual'). Solid teal so "in motion" reads stronger than the bordered
		// "requested" state.
		manual_in_progress: {
			label: "Being fixed by expert",
			className: "bg-teal text-white hover:bg-teal-hover",
		},
		// The expert resolved their ticket by hand; the next crawl confirms it.
		// NOT "Applied" — that badge means the app changed the site (and offers
		// Revert, which an expert fix has no snapshot for). NOT coral either:
		// coral is the app's primary-action slot, which is the wrong meaning for
		// a terminal status. (It also used to be repainted per-tenant by #959,
		// so this badge rendered whatever the client's brand colour was; that
		// injection is reverted, but the semantic reason stands on its own and
		// is why this stays green.) Solid green,
		// darkened past the --status-success token so white text passes WCAG AA
		// at badge size (142 76% 30% → 4.54:1 light; 142 71% 31% → 4.52:1 dark —
		// the token's own lightness only reaches 3.33:1 / 2.30:1).
		manual_applied: {
			label: "Fixed by expert",
			className:
				"bg-[hsl(142_76%_30%)] dark:bg-[hsl(142_71%_31%)] text-white " +
				"hover:bg-[hsl(142_76%_26%)] dark:hover:bg-[hsl(142_71%_27%)]",
		},
		needs_attention: {
			label: "Needs attention",
			className: "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 hover:bg-amber-100",
		},
	};

const fallbackConfig = {
	label: "Unknown",
	className: "bg-gray-300 text-foreground",
};

const largeImageResultConfig: Record<
	LargeImageResultState,
	{ label: string; className: string }
> = {
	applied: {
		label: "Applied",
		className: "bg-teal text-white hover:bg-teal-hover",
	},
	skipped: {
		label: "Skipped",
		className: "bg-muted text-foreground hover:bg-muted",
	},
	protected: {
		label: "Protected",
		className: "bg-coral-light text-coral-dark hover:bg-coral-light",
	},
	failed: {
		label: "Failed",
		className: "bg-destructive text-white hover:bg-destructive/90",
	},
};

const reviewOnlyConfig: Partial<
	Record<IssueUiStatus, { label: string; className: string }>
> = {
	pending: {
		label: "Review only",
		className:
			"border border-coral/40 bg-coral-light text-coral-dark hover:bg-coral-light",
	},
	rejected: {
		label: "Reviewed",
		className:
			"border border-teal/40 bg-teal-light text-teal-dark hover:bg-teal-light",
	},
	skipped: {
		label: "Reviewed",
		className:
			"border border-teal/40 bg-teal-light text-teal-dark hover:bg-teal-light",
	},
};

export function StatusBadge({
	status,
	size = "md",
	resultState = null,
	reviewOnly = false,
}: StatusBadgeProps) {
	const config = resultState
		? largeImageResultConfig[resultState]
		: (reviewOnly ? reviewOnlyConfig[status] : undefined) ??
			statusConfig[status] ??
			fallbackConfig;

	return (
		<Badge
			className={cn(config.className, size === "sm" && "text-xs px-1.5 py-0.5")}
		>
			{config.label}
		</Badge>
	);
}
